/**
 * Defender Service
 *
 * Classification/determination labels and options, plus the one direct
 * write-back the portal still performs itself (incident comments, used by
 * the notes feature). Closing an incident in Defender happens server-side
 * inside analyst-api updateStatus, behind the closure guard; the direct
 * /resolve and /alerts/close paths that bypassed it were removed 2026-10-07
 * (AB#154).
 *
 * API endpoint:
 * - POST /api/defender/:tenantId/incidents/:incidentId/comment
 */

import type {
  DefenderClassification,
  DefenderDetermination,
} from '../data/types';
import { apiPost, type ApiResponse } from './api';

// ============================================================================
// TYPES
// ============================================================================

export interface DefenderActionResult {
  success: boolean;
  timestamp: string;
  message: string;
  defenderId?: string; // ID of the created/updated resource in Defender
}

// ============================================================================
// ALERT ACTIONS
// ============================================================================

/**
 * Closes an alert in the customer's Defender environment
 * 
 * @param tenantId - Alexein tenant ID (axn-xxxx)
 * @param alertId - Defender alert ID
 * @param classification - How to classify the alert
 * @param determination - Specific determination
 * @param comment - Optional comment to add
 */

/**
 * Adds a comment to an alert in the customer's Defender environment
 */

/**
 * Updates an alert's status in Defender
 */

// ============================================================================
// INCIDENT ACTIONS
// ============================================================================

/**
 * Updates an incident's status in the customer's Defender environment
 */

/**
 * Resolves an incident in the customer's Defender environment
 * This sets status to 'resolved' and adds classification/determination
 */

/**
 * Adds a comment to an incident in the customer's Defender environment
 */
export async function addDefenderIncidentComment(
  tenantId: string,
  incidentId: string,
  comment: string
): Promise<ApiResponse<DefenderActionResult>> {
  return apiPost<DefenderActionResult>(
    `/defender/${tenantId}/incidents/${incidentId}/comment`,
    { comment }
  );
}

/**
 * General incident update - can update multiple fields at once
 */

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Gets human-readable classification label
 */
export function getClassificationLabel(classification: DefenderClassification): string {
  const labels: Record<DefenderClassification, string> = {
    unknown: 'Unknown',
    truePositive: 'True Positive',
    falsePositive: 'False Positive',
    informationalExpectedActivity: 'Informational / Expected Activity',
  };
  return labels[classification];
}

/**
 * Gets human-readable determination label
 */
export function getDeterminationLabel(determination: DefenderDetermination): string {
  const labels: Record<DefenderDetermination, string> = {
    unknown: 'Unknown',
    multiStagedAttack: 'Multi-Staged Attack',
    maliciousUserActivity: 'Malicious User Activity',
    compromisedAccount: 'Compromised Account',
    malware: 'Malware',
    phishing: 'Phishing',
    unwantedSoftware: 'Unwanted Software',
    apt: 'APT (Advanced Persistent Threat)',
    securityTesting: 'Security Testing',
    lineOfBusinessApplication: 'Line of Business Application',
    confirmedUserActivity: 'Confirmed User Activity',
    securityPersonnel: 'Security Personnel',
    notMalicious: 'Not Malicious',
    notEnoughDataToValidate: 'Insufficient Data',
    other: 'Other',
  };
  return labels[determination];
}

/**
 * Gets classification options for UI dropdowns
 */
export function getClassificationOptions(): { value: DefenderClassification; label: string }[] {
  return [
    { value: 'truePositive', label: 'True Positive - Confirmed threat' },
    { value: 'falsePositive', label: 'False Positive - Not a threat' },
    { value: 'informationalExpectedActivity', label: 'Informational / Expected Activity' },
    { value: 'unknown', label: 'Unknown - Unable to determine' },
  ];
}

/**
 * Gets determination options based on classification
 * Graph API requires specific determination values for each classification type
 */
export function getDeterminationOptions(
  classification: DefenderClassification
): { value: DefenderDetermination; label: string }[] {
  if (classification === 'truePositive') {
    return [
      { value: 'malware', label: 'Malware' },
      { value: 'phishing', label: 'Phishing' },
      { value: 'compromisedAccount', label: 'Compromised Account' },
      { value: 'multiStagedAttack', label: 'Multi-Staged Attack' },
      { value: 'maliciousUserActivity', label: 'Malicious User Activity' },
      { value: 'unwantedSoftware', label: 'Unwanted Software' },
      { value: 'apt', label: 'APT (Advanced Persistent Threat)' },
      { value: 'other', label: 'Other' },
    ];
  }
  
  if (classification === 'falsePositive') {
    return [
      { value: 'notMalicious', label: 'Not Malicious' },
      { value: 'notEnoughDataToValidate', label: 'Insufficient Data to Validate' },
      { value: 'other', label: 'Other' },
    ];
  }
  
  if (classification === 'informationalExpectedActivity') {
    return [
      { value: 'securityTesting', label: 'Security Testing' },
      { value: 'lineOfBusinessApplication', label: 'Line of Business Application' },
      { value: 'confirmedUserActivity', label: 'Confirmed User Activity' },
      { value: 'securityPersonnel', label: 'Security Personnel' },
      { value: 'other', label: 'Other' },
    ];
  }
  
  return [{ value: 'unknown', label: 'Unknown' }];
}
